import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface InterviewState {
  active: boolean;
  listening: boolean;
  processing: boolean;
  questionCount: number;
  maxQuestions: number;
  currentQuestion: string;
  candidateName: string;
  interviewType: string;
  micMuted: boolean;
  sessionId?: string;
  feedbackData?: any;
  messages: Array<{
    id: string;
    sender: 'ai' | 'user';
    text: string;
    timestamp: string;
  }>;
}

const initialState: InterviewState = {
  active: false,
  listening: false,
  processing: false,
  questionCount: 0,
  maxQuestions: 10,
  currentQuestion: '',
  candidateName: '',
  interviewType: 'general',
  micMuted: false,
  messages: [],
};

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    startInterview: (state, action: PayloadAction<{ candidateName?: string; sessionId?: string }>) => {
      state.active = true;
      state.questionCount = 1;
      state.candidateName = action.payload.candidateName || '';
      state.sessionId = action.payload.sessionId;
      state.messages = [];
    },
    
    endInterview: (state) => {
      state.active = false;
      state.listening = false;
      state.processing = false;
      state.questionCount = 0;
      state.currentQuestion = '';
    },
    
    setListening: (state, action: PayloadAction<boolean>) => {
      state.listening = action.payload;
    },
    
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.processing = action.payload;
    },
    
    setMicMuted: (state, action: PayloadAction<boolean>) => {
      state.micMuted = action.payload;
    },
    
    setCurrentQuestion: (state, action: PayloadAction<string>) => {
      state.currentQuestion = action.payload;
    },
    
    incrementQuestionCount: (state) => {
      state.questionCount += 1;
    },
    
    addMessage: (state, action: PayloadAction<{
      sender: 'ai' | 'user';
      text: string;
    }>) => {
      const message = {
        id: Date.now().toString(),
        sender: action.payload.sender,
        text: action.payload.text,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      state.messages.push(message);
    },
    
    updateUserMessage: (state, action: PayloadAction<{
      id: string;
      text: string;
    }>) => {
      const messageIndex = state.messages.findIndex(msg => msg.id === action.payload.id);
      if (messageIndex >= 0) {
        state.messages[messageIndex].text = action.payload.text;
      }
    },
    
    setFeedbackData: (state, action: PayloadAction<any>) => {
      state.feedbackData = action.payload;
    },
    
    resetInterview: () => initialState,
  },
});

export const {
  startInterview,
  endInterview,
  setListening,
  setProcessing,
  setMicMuted,
  setCurrentQuestion,
  incrementQuestionCount,
  addMessage,
  updateUserMessage,
  setFeedbackData,
  resetInterview,
} = interviewSlice.actions;

export default interviewSlice.reducer;
